/* eslint-disable sort-imports */
/**
 * Upload dialog — the stateful half.
 *
 * Two ingest paths, routed by file type - never by hope, because a binary run
 * through the wrong parser produces a wall of nonsense errors instead of a
 * usable message:
 *
 *   .xlsx / .xls / .csv -> POST to /LaborStandards/parse-recipe. The server
 *                   reads an SAP PP-PI master recipe export ("sap") or a
 *                   long-format sheet with one row per phase ("long") and
 *                   returns the standards it would build: one per product and
 *                   recipe group, with the work center on each OPERATION.
 *                   A REVIEW step then maps unknown work centers to the
 *                   company's, picks a job role per work center for SAP files
 *                   (which carry none), and shows what will be created or
 *                   updated before anything is saved.
 *   .pdf         -> POST to /LaborStandards/parse-pdf, then a REVIEW step
 *                   (product + work centre binding, phase job roles - prefilled
 *                   from the company's configured work phases - and editable
 *                   operation names) before anything is saved
 *   anything else-> refused outright, with no parse attempt
 *
 * Both paths save through the one save mechanism
 * (saveStandards -> laborStandardsApi.create/update).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";

import {
	JobOrderParseResult,
	LaborStandard,
	laborStandardsApi,
	OperationType,
	ParseRecipeResult,
} from "../../../../services/labor-standards-api";
import { Product, productsApi } from "../../../../services/products-api";
import axios from "../../../../utils/api-client";
import { StoreType } from "../../../../utils/types";
import {
	buildPdfPayload,
	buildRecipeWrites,
	classifyFile,
	defaultRoleForPhase,
	DistinctPhase,
	distinctPhasesOf,
	FileKind,
	groupRecipesByProduct,
	MasterJob,
	MasterWorkcenter,
	resolveWorkstationWith,
	StandardWrite,
	SUPPORTED_COPY,
	workPhaseRoleOf,
	workstationsNeedingRoleOf,
} from "../utils/recipe-mapping";

export interface UseRecipeUploadArgs {
	open: boolean;
	onClose: () => void;
	onUploadSuccess: () => void;
	// Every operation of a labor standard runs on a work centre, so the review
	// steps need the work-centre list (the PDF step to bind one, the recipe step
	// to map unknown ones). The page already loads it; when it is not passed down
	// we fetch it the same way the page does.
	workcenters?: string[];
}

export interface UploadStats {
	created: number;
	updated: number;
	skipped: number;
	failed: number;
}

const NO_STATS: UploadStats = { created: 0, updated: 0, skipped: 0, failed: 0 };

export function useRecipeUpload({ open, onClose, onUploadSuccess, workcenters }: UseRecipeUploadArgs) {
	const userId = useSelector((state: StoreType) => state.user.userData.id);

	const [file, setFile] = useState<File | null>(null);
	const [fileKind, setFileKind] = useState<FileKind | null>(null);
	// Recipe (spreadsheet) review state
	const [recipeLoading, setRecipeLoading] = useState(false);
	const [recipeResult, setRecipeResult] = useState<ParseRecipeResult | null>(null);
	/** Raw spreadsheet work-centre text -> one of the company's work centres. */
	const [workstationMap, setWorkstationMap] = useState<Record<string, string>>({});
	/** Work centre -> job role id, for phases the sheet left without a role (SAP). */
	const [workstationRoles, setWorkstationRoles] = useState<Record<string, number | "">>({});
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
	const [existingStandards, setExistingStandards] = useState<LaborStandard[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [uploadStats, setUploadStats] = useState<UploadStats>(NO_STATS);

	// Master data for the PDF review step
	const [fetchedWorkcenters, setFetchedWorkcenters] = useState<string[]>([]);
	const [jobRoles, setJobRoles] = useState<MasterJob[]>([]);

	// PDF review state
	const [pdfLoading, setPdfLoading] = useState(false);
	const [pdfResult, setPdfResult] = useState<JobOrderParseResult | null>(null);
	const [operationNames, setOperationNames] = useState<Record<number, string>>({});
	const [phaseRoles, setPhaseRoles] = useState<Record<string, string>>({});
	const [reviewProductId, setReviewProductId] = useState<number | "">("");
	const [reviewWorkcenter, setReviewWorkcenter] = useState("");

	const workcenterOptions = useMemo(
		() => (workcenters && workcenters.length > 0 ? workcenters : fetchedWorkcenters),
		[workcenters, fetchedWorkcenters]
	);

	// Fetch operation types, existing standards, and products when dialog opens
	useEffect(() => {
		if (open) {
			laborStandardsApi.getOperationTypes().then(setOperationTypes).catch(console.error);
			laborStandardsApi.getAll().then(setExistingStandards).catch(console.error);
			productsApi.getAll().then(setProducts).catch(console.error);
		}
	}, [open]);

	// Work centres - only when the parent did not hand them down.
	useEffect(() => {
		if (!open || (workcenters && workcenters.length > 0)) return;
		axios
			.get<MasterWorkcenter[]>(`${process.env.REACT_APP_API_END_POINT}/WorkCenters/GetWorkcentersByUser`)
			.then((res) => setFetchedWorkcenters((res.data || []).map((wc) => wc.name).sort()))
			.catch((err) => console.error("Failed to fetch workcenters:", err));
	}, [open, workcenters]);

	// Job roles for the phase mapping (same endpoint the page uses).
	useEffect(() => {
		if (!open) return;
		axios
			.get<MasterJob[]>(`${process.env.REACT_APP_API_END_POINT}/Jobs/GetJobsByUser`, {
				params: { userid: userId },
			})
			.then((res) => setJobRoles([...(res.data || [])].sort((a, b) => a.name.localeCompare(b.name))))
			.catch((err) => console.error("Failed to fetch job roles:", err));
	}, [open, userId]);

	// .xlsx / .xls / .csv -> server-side recipe parse, then the review step.
	// The company's work-centre names go along so the server can say which of
	// the sheet's work centres it did not recognise.
	const parseRecipeFile = useCallback(
		async (recipeFile: File) => {
			setRecipeLoading(true);
			try {
				const result = await laborStandardsApi.parseRecipe(recipeFile, {
					knownWorkstations: workcenterOptions,
				});
				setRecipeResult(result);
				setWorkstationMap({});
				setWorkstationRoles({});
				setError(
					result.standards.length === 0
						? "No labor standards could be read from that file. Check the warnings below."
						: null
				);
			} catch (err) {
				console.error("Failed to parse recipe file:", err);
				setError("Could not read that spreadsheet. The file could not be parsed on the server.");
			} finally {
				setRecipeLoading(false);
			}
		},
		[workcenterOptions]
	);

	// .pdf -> server-side parse, then the review step.
	const parsePdfFile = useCallback(async (pdfFile: File) => {
		setPdfLoading(true);
		try {
			const result = await laborStandardsApi.parsePdf(pdfFile);
			setPdfResult(result);
			const names: Record<number, string> = {};
			result.operations.forEach((op) => {
				names[op.sequence] = op.name;
			});
			setOperationNames(names);
			setReviewProductId(result.matchedProductId || "");
			setError(null);
		} catch (err) {
			console.error("Failed to parse job-order PDF:", err);
			setError("Could not read that PDF. The document could not be parsed on the server.");
		} finally {
			setPdfLoading(false);
		}
	}, []);

	const resetParseState = useCallback(() => {
		setError(null);
		setSuccessMessage(null);
		setRecipeResult(null);
		setWorkstationMap({});
		setWorkstationRoles({});
		setPdfResult(null);
		setOperationNames({});
		setPhaseRoles({});
		setReviewProductId("");
		setUploadStats(NO_STATS);
	}, []);

	// Handle file drop
	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (acceptedFiles.length === 0) return;

			const droppedFile = acceptedFiles[0];
			const kind = classifyFile(droppedFile);
			resetParseState();

			if (kind === "unsupported") {
				setFile(null);
				setFileKind(null);
				setError(`"${droppedFile.name}" is not a supported file type. ${SUPPORTED_COPY}.`);
				return;
			}

			setFile(droppedFile);
			setFileKind(kind);

			if (kind === "pdf") parsePdfFile(droppedFile);
			else parseRecipeFile(droppedFile);
		},
		[parseRecipeFile, parsePdfFile, resetParseState]
	);

	// Files the dropzone filters out never reach onDrop - say so plainly rather
	// than swallowing them.
	const onDropRejected = useCallback(
		(rejections: FileRejection[]) => {
			resetParseState();
			setFile(null);
			setFileKind(null);
			const name = rejections[0]?.file?.name;
			setError(name ? `"${name}" is not a supported file type. ${SUPPORTED_COPY}.` : `${SUPPORTED_COPY}.`);
		},
		[resetParseState]
	);

	const dropzone = useDropzone({
		onDrop,
		onDropRejected,
		accept: {
			"text/csv": [".csv"],
			"application/vnd.ms-excel": [".xls"],
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
			"application/pdf": [".pdf"],
		},
		multiple: false,
	});

	// ---- PDF review derivations ----

	const isJobOrder = pdfResult?.detectedFormat === "holman-job-order";
	const distinctPhases = useMemo(() => distinctPhasesOf(pdfResult), [pdfResult]);
	const workPhaseRole = useCallback((phase: DistinctPhase): string => workPhaseRoleOf(phase, jobRoles), [jobRoles]);

	// Seed a role per phase once both sides are known; never overwrite a choice
	// the user has already made. A configured work phase's default role wins;
	// only an unconfigured code falls back to guessing from the description.
	useEffect(() => {
		if (distinctPhases.length === 0 || jobRoles.length === 0) return;
		setPhaseRoles((prev) => {
			const next = { ...prev };
			let changed = false;
			distinctPhases.forEach((phase) => {
				if (!next[phase.phaseCode]) {
					next[phase.phaseCode] =
						workPhaseRole(phase) || defaultRoleForPhase(`${phase.phaseCode} ${phase.description}`, jobRoles);
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [distinctPhases, jobRoles, workPhaseRole]);

	const unmappedPhases = useMemo(
		() => distinctPhases.filter((phase) => !phaseRoles[phase.phaseCode]),
		[distinctPhases, phaseRoles]
	);

	// A phase reads as auto-mapped only when the role actually came from its work
	// phase and the user has not changed it.
	const autoMappedCount = useMemo(
		() =>
			distinctPhases.filter((phase) => {
				const suggestion = workPhaseRole(phase);
				return Boolean(suggestion) && phaseRoles[phase.phaseCode] === suggestion;
			}).length,
		[distinctPhases, phaseRoles, workPhaseRole]
	);
	const allAutoMapped = distinctPhases.length > 0 && autoMappedCount === distinctPhases.length;
	const effectiveProductId = pdfResult?.matchedProductId || (reviewProductId === "" ? null : reviewProductId);

	const canSavePdf = Boolean(
		isJobOrder &&
			pdfResult &&
			pdfResult.operations.length > 0 &&
			effectiveProductId &&
			reviewWorkcenter &&
			unmappedPhases.length === 0
	);

	const setPhaseRole = useCallback((phaseCode: string, role: string) => {
		setPhaseRoles((prev) => ({ ...prev, [phaseCode]: role }));
	}, []);
	const setOperationName = useCallback((sequence: number, name: string) => {
		setOperationNames((prev) => ({ ...prev, [sequence]: name }));
	}, []);

	// ---- Recipe review derivations ----

	const resolveWorkstation = useMemo(() => resolveWorkstationWith(workstationMap), [workstationMap]);
	const recipeStandards = useMemo(() => recipeResult?.standards ?? [], [recipeResult]);

	// Work centres the sheet uses that the company does not have (after the
	// user's mapping). Every one has to be mapped before anything is saved.
	const unresolvedWorkstations = useMemo(
		() => (recipeResult?.unknownWorkstations ?? []).filter((raw) => !workstationMap[raw]),
		[recipeResult, workstationMap]
	);

	const workstationsNeedingRole = useMemo(
		() => workstationsNeedingRoleOf(recipeStandards, resolveWorkstation),
		[recipeStandards, resolveWorkstation]
	);
	const unmappedRoleWorkstations = useMemo(
		() => workstationsNeedingRole.filter((name) => !workstationRoles[name]),
		[workstationsNeedingRole, workstationRoles]
	);

	const canSaveRecipe = Boolean(
		recipeResult && recipeStandards.length > 0 && unresolvedWorkstations.length === 0 && unmappedRoleWorkstations.length === 0
	);

	const recipeByProduct = useMemo(() => groupRecipesByProduct(recipeStandards), [recipeStandards]);

	const setWorkstationMapping = useCallback((raw: string, value: string) => {
		setWorkstationMap((prev) => {
			const next = { ...prev };
			if (value) next[raw] = value;
			else delete next[raw];
			return next;
		});
	}, []);
	const setWorkstationRole = useCallback((name: string, roleId: number | "") => {
		setWorkstationRoles((prev) => ({ ...prev, [name]: roleId || "" }));
	}, []);

	// ---- The one save mechanism, shared by both ingest paths ----

	const saveStandards = useCallback(
		async (writes: StandardWrite[]) => {
			if (writes.length === 0) return;

			setUploading(true);
			setError(null);
			const stats: UploadStats = { created: 0, updated: 0, skipped: 0, failed: 0 };

			try {
				for (const { payload, existingId } of writes) {
					try {
						// The standard this replaces: the one the server matched for a
						// recipe, else the same product + recipe group + batch size.
						const existing =
							(existingId ? existingStandards.find((es) => es.id === existingId) : undefined) ??
							existingStandards.find(
								(es) =>
									es.sku === payload.sku &&
									(es.recipeGroup ?? null) === (payload.recipeGroup ?? null) &&
									es.batchSize === payload.batchSize
							);

						if (existing) {
							// Update existing
							await laborStandardsApi.update(existing.id, { ...payload, id: existing.id });
							stats.updated++;
						} else if (existingId) {
							await laborStandardsApi.update(existingId, { ...payload, id: existingId });
							stats.updated++;
						} else {
							// Create new
							await laborStandardsApi.create(payload);
							stats.created++;
						}
					} catch (err) {
						console.error("Failed to upload standard:", payload, err);
						stats.failed++;
					}
				}

				setUploadStats(stats);
				setSuccessMessage(`Upload complete: ${stats.created} created, ${stats.updated} updated, ${stats.failed} failed`);

				if (stats.created > 0 || stats.updated > 0) {
					setTimeout(() => {
						onUploadSuccess();
					}, 1500);
				}
			} catch (err) {
				console.error("Upload failed:", err);
				setError("Failed to upload labor standards");
			} finally {
				setUploading(false);
			}
		},
		[existingStandards, onUploadSuccess]
	);

	const handleUpload = useCallback(async () => {
		if (fileKind === "pdf") {
			if (pdfResult) {
				await saveStandards([
					{
						payload: buildPdfPayload({
							pdfResult,
							operationNames,
							operationTypes,
							phaseRoles,
							products,
							productId: effectiveProductId,
							workcenter: reviewWorkcenter,
						}),
					},
				]);
			}
			return;
		}
		await saveStandards(
			buildRecipeWrites({
				standards: recipeStandards,
				resolveWorkstation,
				operationTypes,
				workstationRoles,
				jobRoles,
				fileName: file?.name,
			})
		);
	}, [
		fileKind,
		pdfResult,
		operationNames,
		operationTypes,
		phaseRoles,
		products,
		effectiveProductId,
		reviewWorkcenter,
		recipeStandards,
		resolveWorkstation,
		workstationRoles,
		jobRoles,
		file,
		saveStandards,
	]);
	const startUpload = useCallback(() => void handleUpload(), [handleUpload]);

	// Reset state on close
	const handleClose = useCallback(() => {
		setFile(null);
		setFileKind(null);
		setReviewWorkcenter("");
		resetParseState();
		onClose();
	}, [resetParseState, onClose]);

	const handleClearFile = useCallback(() => {
		setFile(null);
		setFileKind(null);
		resetParseState();
	}, [resetParseState]);

	const recipeUpdateCount = recipeStandards.filter((std) => std.existingStandardId).length;

	const primaryDisabled = uploading || (fileKind === "pdf" ? !canSavePdf : !canSaveRecipe);
	const primaryLabel = uploading
		? "Saving..."
		: fileKind === "pdf"
			? "Create labor standard"
			: recipeStandards.length === 1
				? recipeUpdateCount === 1
					? "Update labor standard"
					: "Create labor standard"
				: `Save ${recipeStandards.length} standards`;

	return {
		file,
		fileKind,
		error,
		successMessage,
		uploading,
		uploadStats,
		dropzone,
		products,
		jobRoles,
		workcenterOptions,
		// PDF path
		pdfLoading,
		pdfResult,
		isJobOrder,
		distinctPhases,
		workPhaseRole,
		phaseRoles,
		setPhaseRole,
		unmappedPhases,
		autoMappedCount,
		allAutoMapped,
		operationNames,
		setOperationName,
		reviewProductId,
		setReviewProductId,
		reviewWorkcenter,
		setReviewWorkcenter,
		// Recipe path
		recipeLoading,
		recipeResult,
		recipeStandards,
		recipeByProduct,
		recipeUpdateCount,
		resolveWorkstation,
		workstationMap,
		setWorkstationMapping,
		unresolvedWorkstations,
		workstationsNeedingRole,
		workstationRoles,
		setWorkstationRole,
		unmappedRoleWorkstations,
		canSaveRecipe,
		// Actions
		startUpload,
		handleClose,
		handleClearFile,
		primaryDisabled,
		primaryLabel,
	};
}

export type RecipeUpload = ReturnType<typeof useRecipeUpload>;
